/**
 * LiveStatsForm: edit form for the `live-stats` dynamic block.
 *
 * data: { sectionHeading?, visibleStats? }
 *
 * Two controls:
 *   1. Section heading text input.
 *   2. Ordered stat selector: all available stats shown as checkboxes.
 *      Checked stats appear first (in their current display order) with ↑↓
 *      arrow buttons to reorder. Unchecked stats appear below in canonical
 *      order. Checking a stat appends it to the end of visibleStats; the ↑↓
 *      buttons swap adjacent checked entries.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, ButtonGroup, Form } from 'react-bootstrap';

import useIsFlagEnabled from '@/shared/hooks/useIsFlagEnabled';

// Stats that require a feature flag to be enabled. Absent = always available.
const STAT_FLAGS = {
    mentors: 'mentoring',
};

const ALL_STAT_KEYS = [
    'organizations',
    'connections',
    'sectors',
    'regions',
    'avgDegree',
    'mentors',
    'users',
];

const STAT_LABELS = {
    organizations: 'Organizations',
    connections: 'Connections',
    sectors: 'Active Sectors',
    regions: 'Regions Covered',
    avgDegree: 'Avg. Connections per Org',
    mentors: 'Mentors Available',
    users: 'Ecosystem Members',
};

export default function LiveStatsForm({ data, onChange }) {
    const d = data || {};
    const visibleStats = Array.isArray(d.visibleStats) ? d.visibleStats : [];

    const isFlagEnabled = useIsFlagEnabled();

    // Keys that are checked AND whose flag is currently on.
    const effectiveVisible = visibleStats.filter(
        (k) => !STAT_FLAGS[k] || isFlagEnabled(STAT_FLAGS[k])
    );

    // Unchecked: never in visibleStats, OR in visibleStats but flag-disabled.
    const uncheckedKeys = ALL_STAT_KEYS.filter(
        (k) => !effectiveVisible.includes(k)
    );

    const displayOrder = [...effectiveVisible, ...uncheckedKeys];

    const update = (nextVisible) =>
        onChange({ ...d, visibleStats: nextVisible });

    const toggle = (key, checked) => {
        if (checked) {
            update([...visibleStats, key]);
        } else {
            update(visibleStats.filter((k) => k !== key));
        }
    };

    const moveUp = (key) => {
        const idx = visibleStats.indexOf(key);
        if (idx <= 0) return;
        const next = [...visibleStats];
        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
        update(next);
    };

    const moveDown = (key) => {
        const idx = visibleStats.indexOf(key);
        if (idx < 0 || idx >= visibleStats.length - 1) return;
        const next = [...visibleStats];
        [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
        update(next);
    };

    return (
        <>
            <Form.Group controlId="live-stats-heading" className="mb-4">
                <Form.Label>Section heading</Form.Label>
                <Form.Control
                    type="text"
                    value={d.sectionHeading || ''}
                    onChange={(e) =>
                        onChange({ ...d, sectionHeading: e.target.value })
                    }
                    placeholder="A Growing and Thriving Ecosystem"
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Visible stats</Form.Label>
                <Form.Text muted className="d-block mb-2">
                    Check stats to show them. Use arrows to set display order.
                </Form.Text>
                <div className="d-flex flex-column gap-2">
                    {displayOrder.map((key) => {
                        const flagKey = STAT_FLAGS[key];
                        const flagEnabled = !flagKey || isFlagEnabled(flagKey);
                        const isChecked =
                            flagEnabled && visibleStats.includes(key);
                        const idx = effectiveVisible.indexOf(key);
                        const isFirst = idx === 0;
                        const isLast = idx === effectiveVisible.length - 1;

                        return (
                            <div
                                key={key}
                                className="d-flex align-items-center gap-2"
                            >
                                <Form.Check
                                    type="checkbox"
                                    id={`live-stat-${key}`}
                                    checked={isChecked}
                                    disabled={!flagEnabled}
                                    onChange={(e) =>
                                        toggle(key, e.target.checked)
                                    }
                                    label={
                                        <span
                                            className={
                                                !flagEnabled ? 'text-muted' : ''
                                            }
                                        >
                                            {STAT_LABELS[key]}
                                            {!flagEnabled && (
                                                <Badge
                                                    bg="secondary"
                                                    className="ms-2 fw-normal"
                                                >
                                                    Feature disabled
                                                </Badge>
                                            )}
                                        </span>
                                    }
                                    className="flex-grow-1 mb-0"
                                />
                                {isChecked && (
                                    <ButtonGroup size="sm">
                                        <Button
                                            type="button"
                                            variant="outline-secondary"
                                            onClick={() => moveUp(key)}
                                            disabled={isFirst}
                                            aria-label={`Move ${STAT_LABELS[key]} up`}
                                        >
                                            ↑
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline-secondary"
                                            onClick={() => moveDown(key)}
                                            disabled={isLast}
                                            aria-label={`Move ${STAT_LABELS[key]} down`}
                                        >
                                            ↓
                                        </Button>
                                    </ButtonGroup>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Form.Group>
        </>
    );
}

LiveStatsForm.propTypes = {
    data: PropTypes.shape({
        sectionHeading: PropTypes.string,
        visibleStats: PropTypes.arrayOf(PropTypes.string),
    }),
    onChange: PropTypes.func.isRequired,
};

LiveStatsForm.defaultProps = {
    data: {},
};
